import { api } from "@/shared/api";
import type { components } from "@/lib/api/generated";
import type { Provider as MediaProvider, Tier as MediaTier } from "@/entities/media-provider/api";
// V6 OverlayConfig 原语 2026-06-23 抽到 @/shared/lib/overlay/overlayConfig(海报+视频成片编辑共用,
// FSD:features 互不 import)。import 供本文件内部使用 + 下方 re-export 保海报侧导入零改动。
import {
  DEFAULT_OVERLAY,
  type OverlayConfig,
  type OverlayItem,
} from "@/shared/lib/overlay/overlayConfig";

// local-only: backend schema gap — PosterTemplate ratio enum used in upsert/list responses;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export type PosterTemplateRatio = "9:16" | "1:1" | "4:5" | "16:9" | "3:4";
// local-only: backend schema gap — PosterTemplate status enum (DRAFT/PUBLISHED/ARCHIVED);
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export type PosterTemplateStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
// Template provider mirrors generated Provider (incl. MOCK to match poster Provider after V23 migration).
export type PosterTemplateProvider = MediaProvider;

/**
 * v1.5 (2026-05-14): IndustryCode 从 6 enum 改为 string。
 * 权威源是 backend xb_industry.code(IND_* 前缀),由 industryService.list() 拉。
 * 不再硬编码,海报模板 / 场景管理也走 16 个 IND_*。
 */
// local-only: frontend type alias — string typedef for documentation only (no backend enum needed,
// runtime values are dynamic from xb_industry table).
export type IndustryCode = string;

// V26 (2026-05-14) Wave 4.X — 删原 INDUSTRY_PACK_LABELS 11 项 best-effort fallback。
// 所有 caller 已切 industryService DB 拉 (PosterTemplateEditSheet / PosterSceneAdminPage /
// PosterTemplateAdminPage 用 industryCodeToName Map),DB 是 single source of truth。
// 行业 KB 路径仍用 industryKbService.INDUSTRY_LABELS(16 项手维护,跟 DB 一致)。

/** 归一化 bbox。`x/y` 是左上角,`w/h` 宽高,全部 0..1 范围。 */
// local-only: backend schema gap — schemaJson sub-type embedded in PosterTemplateBrief;
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface NormalizedBbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export { DEFAULT_OVERLAY, type OverlayConfig, type OverlayItem };

/** 模板 schema_json 解析后的结构。 */
// local-only: backend schema gap — schemaJson field is JSON-string column server-side;
// frontend deserializes it into typed sub-shape. V24 yaml align via
// 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PosterTemplateSchema {
  version?: number;
  /** V6 canonical — Logo / QR 在画布上的归一化默认位置(由 admin 在拖拽编辑器设) */
  defaultOverlay?: OverlayConfig | null;
  imageDimensions?: { w: number; h: number } | null;
  /** V5 legacy — 老 admin 模板用,V5 migration 之后保留 3 月兼容窗口(2026-08-12 后可删) */
  logoBbox?: NormalizedBbox | null;
  qrBbox?: NormalizedBbox | null;
}

/** 模板 Brief(promptT2i/promptI2i/hasPromptI2i/llmOptimize 随套模板 jinja 路删除)。
 *
 * ★2026-08-26 炸库重建:去掉 `type` —— 海报模板已退役,库里只剩长图一种形态。留一个恒为常量的维度,
 * 唯一后果是缺省值有两个来源(后端 normalizeType + DDL DEFAULT),两边谁都能被单独改瘸。 */
// local-only: backend schema gap — full PosterTemplateBrief DTO returned by gallery + admin
// endpoints not declared in spec. V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md.
export interface PosterTemplateBrief {
  id: number;
  code: string;
  name: string;
  industryCodes: IndustryCode[];
  scene: string;
  style: string;
  provider: PosterTemplateProvider;
  ratio: PosterTemplateRatio;
  /** V7 (2026-05-12) — 模板锁定分辨率档位。套模板时透传给任务,允许租户改。 */
  resolutionTier?: "1K" | "2K" | "3K" | "4K";
  coverImageUrl: string;
  /** V7 (2026-05-12) — 服务端生成的 600px 缩略图签名 URL,gallery 列表优先用。 */
  thumbUrl?: string | null;
  /** schema_json 解析后:IMAGE=overlay 布局(PosterTemplateSchema);LONGIMAGE=长图骨架整包
   *  (形状与验型收口在 longimage/model/templateSchema.ts 的 parseTemplateSchema,别在这里 as)。 */
  schemaJson: PosterTemplateSchema | Record<string, unknown> | null;
  isHot: boolean;
  recentUses: number;
  /** admin 视图独有 */
  status?: PosterTemplateStatus;
  /** admin 视图独有 */
  sortWeight?: number;
  createdAt: string;
  updatedAt: string;
}

// local-only: backend schema gap — common PageResponse<T> generic absent from spec;
// see posterService.ts PageResp note. V24 yaml align.
export interface PageResp<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

// local-only: frontend query params builder for /poster/gallery (URLSearchParams flatten).
export interface ListGalleryParams {
  scene?: string;
  ratio?: string;
  /** V7 (2026-05-12) — 按 tier 过滤,值域 1K/2K/3K/4K 或 "all"。 */
  tier?: "1K" | "2K" | "3K" | "4K" | "all";
  /** 按 provider 过滤(挑模板顺带锁 provider)。 */
  provider?: PosterTemplateProvider | "all";
  search?: string;
  sort?: "hot" | "new" | "uses";
  page?: number;
  size?: number;
}

// local-only: frontend query params builder for /platform/poster-templates (URLSearchParams flatten).
export interface ListAdminParams {
  status?: PosterTemplateStatus | "ALL";
  scene?: string;
  ratio?: PosterTemplateRatio | "all";
  /** V7 (2026-05-12) — 按 tier 过滤。 */
  tier?: "1K" | "2K" | "3K" | "4K" | "all";
  search?: string;
  page?: number;
  size?: number;
}

// local-only: backend schema gap — POST /platform/poster-templates body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-poster.md proposal.
export interface PosterTemplateUpsertRequest {
  /** 可空,后端兜底 tpl_<8hex>。 */
  code?: string;
  /** 可空,后端兜底 "成品图模板 yyyyMMdd-HHmmss"。 */
  name?: string;
  industryCodes: IndustryCode[];
  scene: string;
  style: string;
  provider: PosterTemplateProvider;
  ratio: PosterTemplateRatio;
  /** V7 (2026-05-12) — 必填,模板锁定的分辨率档位(由 canvas scale 映射写入,P5 占位不放松)。 */
  resolutionTier: "1K" | "2K" | "3K" | "4K";
  /** 长图骨架整包(必须经 templateSchema.buildTemplateSchema 裁剪后写入)。 */
  schemaJson?: PosterTemplateSchema | Record<string, unknown>;
  /** 必填(gallery 缩略图)。 */
  coverImageUrl: string;
  sortWeight?: number;
  isHot?: boolean;
}

export type ResolutionTier = MediaTier;

// provider-capability 契约已抽至 entities/media-provider(去 poster 耦合,ADR-0013 consumer-cutover S2)。
// 此处 re-export 保既有类型消费方(video 等)零改;hooks(useProviderCapabilities/Constraints)走 @/entities/media-provider。
export type {
  I2iCapability,
  ThinkingCapability,
  RatioTierCombo,
  ProviderCapability,
  ProviderCapabilitiesResponse,
} from "@/entities/media-provider";

function toQs(params: object): string {
  const qs = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const posterTemplateService = {
  /** 租户工作台模板墙(仅 PUBLISHED, 后端按租户 industry_code OR generic 过滤)。 */
  listGallery: (params: ListGalleryParams = {}) =>
    api.get<PageResp<PosterTemplateBrief>>(`/poster/gallery${toQs(params)}`),

  getOne: (id: number) => api.get<PosterTemplateBrief>(`/poster/gallery/${id}`),

  // -------------------- 平台管理员 --------------------

  listAdmin: (params: ListAdminParams = {}) =>
    api.get<PageResp<PosterTemplateBrief>>(`/platform/poster-templates${toQs(params)}`),

  getOneAdmin: (id: number) => api.get<PosterTemplateBrief>(`/platform/poster-templates/${id}`),

  create: (req: PosterTemplateUpsertRequest) =>
    api.post<PosterTemplateBrief>("/platform/poster-templates", req),

  update: (id: number, req: PosterTemplateUpsertRequest) =>
    api.put<PosterTemplateBrief>(`/platform/poster-templates/${id}`, req),

  publish: (id: number) =>
    api.post<PosterTemplateBrief>(`/platform/poster-templates/${id}/publish`, null),

  archive: (id: number) =>
    api.post<PosterTemplateBrief>(`/platform/poster-templates/${id}/archive`, null),

  backToDraft: (id: number) =>
    api.post<PosterTemplateBrief>(`/platform/poster-templates/${id}/draft`, null),

  /**
   * V7.3 (2026-05-12) — 永久硬删除模板。只允许 ARCHIVED 状态(后端 409 拦截)。清理 MinIO
   * cover + thumb 对象。历史任务不受影响(task 已 snapshot brand/overlay 到 request_payload_json)。
   */
  remove: (id: number) => api.delete<void>(`/platform/poster-templates/${id}`),

  // 平台模板封面上传:走平台域端点(PLATFORM_ADMIN 可达);此前复用租户 /poster/uploads 致 admin 403。
  uploadCover: (file: File) => {
    const fd = new FormData();
    fd.append("kind", "logo");
    fd.append("file", file);
    return api.post<components["schemas"]["UploadResponse"]>(
      "/platform/poster-templates/uploads",
      fd
    );
  },

  /**
   * P2 长图模板 v2:admin 逐块**示例图**上传 —— 走**平台域**端点(`__platform__` 桶,长期可跨租户访问),
   * 而不是租户 `/poster/uploads`(存 admin 自己租户桶、短效、别的租户套用后无权访问)。返回的 objectKey
   * 落进 block `props.refKey`,api 读模板时 `signSchemaImages` 现签成右画布可显示的 refUrl。
   */
  uploadBlockImage: (file: File) => {
    const fd = new FormData();
    fd.append("kind", "ref_image");
    fd.append("file", file);
    return api.post<components["schemas"]["UploadResponse"]>(
      "/platform/poster-templates/uploads",
      fd
    );
  },
};
